import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'

const SENSITIVE_KEY =
  /(authorization|cookie|token|password|secret|credential|phone|number|contact|lead|body|headers)/i
const IGNORED_HTTP_STATUSES = new Set([401, 404])
const IGNORED_HTTP_ERROR_MESSAGE = /\bstatus(?: code)? (?:401|404)\b/i

function optionalNumber(value: unknown, fallback: number) {
  if (typeof value !== 'string') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function scrub(value: unknown, depth = 0): unknown {
  if (depth > 4 || value === null || value === undefined) return value
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1))
  if (typeof value !== 'object') return value

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? '[Filtered]' : scrub(item, depth + 1),
    ]),
  )
}

function isIgnoredHttpError(value: unknown) {
  if (!value || typeof value !== 'object') return false

  const error = value as {
    response?: { status?: unknown }
    status?: unknown
  }
  const status = error.response?.status ?? error.status

  return typeof status === 'number' && IGNORED_HTTP_STATUSES.has(status)
}

export function initSentry(app: App, router: Router) {
  if (!import.meta.env.VITE_SENTRY_DSN || import.meta.env.SSR) return

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ??
      import.meta.env.VITE_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? __APP_VERSION__,
    dist: import.meta.env.VITE_SENTRY_DIST,
    sendDefaultPii: false,
    replaysSessionSampleRate: optionalNumber(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0.1,
    ),
    replaysOnErrorSampleRate: optionalNumber(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0,
    ),
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracePropagationTargets: [
      'localhost',
      import.meta.env.VITE_DOMAIN,
      /^\//,
    ].filter(Boolean),
    tracesSampleRate: optionalNumber(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0,
    ),
    initialScope: {
      tags: {
        app: 'letsdial-web',
        runtime: 'browser',
        client: import.meta.env.VITE_CLIENT,
        client_domain: import.meta.env.VITE_DOMAIN,
      },
    },
    beforeSend(event, hint) {
      const hasIgnoredHttpError = event.exception?.values?.some((exception) =>
        IGNORED_HTTP_ERROR_MESSAGE.test(exception.value ?? ''),
      )

      if (isIgnoredHttpError(hint.originalException) || hasIgnoredHttpError) {
        return null
      }

      if (event.request) {
        delete event.request.headers
        delete event.request.cookies
        delete event.request.data
        delete event.request.query_string
      }
      event.contexts = scrub(event.contexts) as typeof event.contexts
      event.extra = scrub(event.extra) as typeof event.extra
      return event
    },
  })
}
